import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "./Navbar";
import ProgressChart from "./ProgressChart";
import GamificationSystem from "./GamificationSystem";
import SpacedRepetitionSystem from "./SpacedRepetitionSystem";
import { BookOpen, Flame, Clock, Check, Brain, Target, Trophy } from "lucide-react";

const TOTAL_LESSONS = 24;
const TOTAL_VOCAB_WORDS = 100;

function Dashboard() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        navigate("/login");
        return;
      }

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          setUserData(snap.data());
        } else {
          setError("User data not found. Please complete onboarding.");
          navigate("/onboarding");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, navigate]);

  const handleContinue = () => {
    const completed = userData?.completedLessons || [];
    const allLessonIds = Array.from({ length: TOTAL_LESSONS }, (_, i) => `lesson${i + 1}`);
    const nextLessonId = allLessonIds.find(lId => !completed.includes(lId));

    if (nextLessonId) {
      navigate(`/lesson/${nextLessonId}`);
    } else {
      navigate("/lessons");
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100 flex items-center justify-center">
          <div className="text-center p-10 bg-white rounded-3xl shadow-2xl border-4 border-red-200">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-red-600 mx-auto mb-4"></div>
            <p className="text-xl font-semibold text-gray-700">Loading your dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100 flex items-center justify-center">
          <div className="text-center p-10 bg-red-50 rounded-3xl shadow-2xl border-4 border-red-300">
            <p className="text-xl font-semibold text-red-800">{error}</p>
            <button
              onClick={() => navigate("/onboarding")}
              className="mt-6 bg-red-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all duration-300 shadow-lg"
            >
              Go to Onboarding
            </button>
          </div>
        </div>
      </>
    );
  }

  const completedCount = userData?.completedLessons?.length || 0;
  const vocabPracticed = Object.keys(userData?.vocabularyProgress || {}).length;
  const percent = Math.round((completedCount / TOTAL_LESSONS) * 100);
  const vocabPercent = Math.round((vocabPracticed / TOTAL_VOCAB_WORDS) * 100);

  const stats = [
    {
      title: "Lessons Completed",
      value: completedCount,
      total: TOTAL_LESSONS,
      icon: <BookOpen className="w-10 h-10 text-red-600" />,
      color: "red",
      percent: percent
    },
    {
      title: "Vocabulary Words",
      value: vocabPracticed,
      total: TOTAL_VOCAB_WORDS,
      icon: <Check className="w-10 h-10 text-orange-600" />,
      color: "orange",
      percent: vocabPercent
    },
    {
      title: "Study Streak",
      value: userData?.studyStreak || 0,
      total: 30,
      icon: <Flame className="w-10 h-10 text-amber-600" />,
      color: "amber",
      percent: Math.min(((userData?.studyStreak || 0) / 30) * 100, 100)
    },
    {
      title: "Total Study Time",
      value: userData?.totalStudyTime || 0,
      total: 100,
      icon: <Clock className="w-10 h-10 text-green-600" />,
      color: "green",
      percent: Math.min(((userData?.totalStudyTime || 0) / 100) * 100, 100)
    }
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <img
                    src="/korlish.png"
                    alt="Korlish Logo"
                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-full shadow-2xl ring-4 ring-white relative z-10"
                  />
                  <div className="absolute -top-2 -right-2 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-bounce">
                    🇰🇷
                  </div>
                </div>
                <div className="text-center sm:text-left">
                  <h1 className="text-2xl sm:text-4xl font-black text-gray-900">
                    Welcome back, {userData?.displayName || "Learner"}!
                  </h1>
                  <div className="mt-2">
                    <p className="text-lg sm:text-2xl text-red-700 font-black mb-1">다시 오신 것을 환영합니다!</p>
                    <p className="text-sm sm:text-lg text-gray-700 font-semibold">
                      Continue your Korean learning journey
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate("/profile")}
                  className="bg-white text-gray-800 px-6 py-3 rounded-2xl font-bold hover:bg-red-50 transition-all duration-300 shadow-lg border-2 border-red-200 hover:border-red-400"
                >
                  View Profile
                </button>
                <button
                  onClick={handleContinue}
                  className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-black text-lg hover:from-red-700 hover:to-orange-700 transform hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-red-500/25"
                >
                  Continue Learning →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-4xl font-black text-gray-900 mb-12 text-center">Your Learning Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br from-${stat.color}-50 to-${stat.color}-100 rounded-3xl shadow-2xl p-8 border-4 border-${stat.color}-200 transform hover:scale-105 transition-all duration-300`}
              >
                <div className="flex items-center justify-between mb-6">
                  {stat.icon}
                  <span className={`text-5xl font-black text-${stat.color}-700`}>
                    {stat.value}
                    {stat.title.includes("Time") ? "m" : ""}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{stat.title}</h3>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full bg-gradient-to-r from-${stat.color}-400 to-${stat.color}-600`}
                    style={{ width: `${stat.percent}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  {stat.percent}% of goal ({stat.value}/{stat.total})
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-4xl font-black text-gray-900 mb-12 text-center">Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-8 border-4 border-red-200 text-center transform hover:scale-105 transition-all duration-300">
              <BookOpen className="w-16 h-16 text-red-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore Lessons</h3>
              <p className="text-gray-700 mb-6">Dive into structured Korean lessons.</p>
              <button
                onClick={() => navigate("/lessons")}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-2xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg"
              >
                Start Learning
              </button>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl shadow-2xl p-8 border-4 border-orange-200 text-center transform hover:scale-105 transition-all duration-300">
              <Check className="w-16 h-16 text-orange-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Practice Quizzes</h3>
              <p className="text-gray-700 mb-6">Test your knowledge with interactive quizzes.</p>
              <button
                onClick={() => navigate("/practice")}
                className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-3 rounded-2xl font-bold hover:from-orange-700 hover:to-amber-700 transition-all duration-300 shadow-lg"
              >
                Start Practicing
              </button>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-green-50 rounded-3xl shadow-2xl p-8 border-4 border-amber-200 text-center transform hover:scale-105 transition-all duration-300">
              <Flame className="w-16 h-16 text-amber-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Expand Vocabulary</h3>
              <p className="text-gray-700 mb-6">Learn new words and phrases daily.</p>
              <button
                onClick={() => navigate("/vocab")}
                className="bg-gradient-to-r from-amber-600 to-green-600 text-white px-6 py-3 rounded-2xl font-bold hover:from-amber-700 hover:to-green-700 transition-all duration-300 shadow-lg"
              >
                Learn Vocab
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="space-y-16">
            {/* Progress Visualization */}
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6 flex items-center">
                <Target className="w-8 h-8 text-red-600 mr-3" />
                Progress Visualization
              </h2>
              <ProgressChart />
            </div>

            {/* Gamification System */}
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6 flex items-center">
                <Trophy className="w-8 h-8 text-amber-600 mr-3" />
                Achievements & Rewards
              </h2>
              <GamificationSystem />
            </div>

            {/* Spaced Repetition */}
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-6 flex items-center">
                <Brain className="w-8 h-8 text-blue-600 mr-3" />
                Smart Review
              </h2>
              <SpacedRepetitionSystem />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;